<template>
  <div class="readings">
    <v-card>
      <v-card-title>
        <v-btn
          v-if="forSpecificCustomer && customerId && can('meter-reading-edit')"
          color="primary"
          outlined
          class="ma-3"
          @click="openCorrections()"
        >
          Correct reading history
        </v-btn>
        <v-container>
          <v-row>
            <v-col class="col-md-3">
              <refresh-button
                :loading="isMeterReadingsRefreshing"
                @click="refreshMeterReadings()"
              />
            </v-col>
            <v-col class="col-md-6">
              <table-filter
                :table-name="'billings'"
                :show-date-filter="false"
                :show-payment-status-filter="true"
                :show-month-filter="!forSpecificCustomer"
                :show-year-range-filter="forSpecificCustomer"
                :label="filterLabel"
                @filter="applyFilters"
              />
            </v-col>
            <v-col class="col-md-3 text-right">
              <export-button
                :title="constructExportTitle()"
                :headers="exportTableHeaders"
                :data-endpoint="constructMeterReadingsUrl()"
                :orientation="'landscape'"
                :row-transform="transformExportRow"
              />
            </v-col>
            <v-spacer />
          </v-row><v-row
            justify="center"
            class="mb-0"
          >
            <v-col class="col-md-6 text-center">
              <search-input
                v-model="search"
                :filters="searchFilters"
                :active-filter="activeSearchFilter"
                @filter-change="onSearchFilterChange"
              />
            </v-col>
          </v-row>
          <v-row class="ma-4">
            <v-col
              class="text-left col-md-6"
            >
              <span
                class="font-weight-light"
              >Total Expected Amount: </span>
              <span v-show="!isMeterReadingsLoading">Ksh {{ totalExpectedAmount | formatCurrency }}</span>
            </v-col>
            <v-col
              class="text-right col-md-6"
            >
              <span
                class="font-weight-light"
              >Total Amount Paid: </span>
              <span v-show="!isMeterReadingsLoading">Ksh {{ totalAmountPaid | formatCurrency }}</span>
            </v-col>
          </v-row>
        </v-container>
      </v-card-title>
      <v-data-table
        :headers="headers"
        :items="meterReadings"
        :loading="isMeterReadingsLoading"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        disable-pagination
        hide-default-footer
      >
        <template v-slot:progress>
          <v-progress-linear
            indeterminate
            class="mt-2"
            rounded
          ></v-progress-linear>
          <p class="text-center mt-1">
            Loading...Please wait
          </p>
        </template>
        <template
          v-slot:body="{
            items,
          }"
        >
          <tbody v-if="meterReadings.length > 0">
            <tr
              v-for="reading in items"
              :key="reading.id"
              :class="{ clickable: clickableRows }"
              v-on="clickableRows ? { click: () => onMeterReadingClick(reading.id) } : {}"
            >
              <td v-if="clickableRows">
                <span class="primary--text font-weight-medium">{{ reading.account_number }}</span>
              </td>
              <td v-if="clickableRows">
                <span class="primary--text font-weight-medium">{{ reading.user_name }}</span>
              </td>
              <td>{{ reading.previous_reading }}</td>
              <td>
                <div class="d-flex align-center">
                  {{ reading.current_reading }}
                  <v-chip
                    v-if="reading.has_correction && can('meter-reading-edit')"
                    x-small
                    class="ml-2"
                    @click.stop="openCorrections(reading)"
                  >
                    Corrected
                  </v-chip>
                  <v-chip
                    v-if="reading.last_reading_edit"
                    x-small
                    label
                    class="ml-2 px-1 edited-chip"
                    @click.stop="viewReadingEditDetails(reading)"
                  >
                    <v-icon
                      x-small
                      left
                      style="margin-right: 2px"
                    >
                      {{ icons.mdiHistory }}
                    </v-icon>
                    Edited
                  </v-chip>
                </div>
              </td>
              <td>
                {{ billedUnits(reading) }}<small
                  v-if="reading.adjusted_units != null"
                  class="d-block"
                >Estimated</small>
              </td>
              <td>{{ reading.month | formatMonth }}</td>
              <td>{{ reading.created_at | formatDate }}</td>
              <td>Ksh {{ reading.bill | formatCurrency }}</td>
              <td>Ksh {{ reading.meter_billings_sum_amount_paid ? reading.meter_billings_sum_amount_paid : 0 | formatCurrency }}</td>
              <td>Ksh {{ reading.meter_billings_sum_credit ? reading.meter_billings_sum_credit : 0 | formatCurrency }}</td>
              <td>Ksh {{ (reading.outstanding_balance != null ? reading.outstanding_balance : calculateBalance(reading.meter_billings_sum_amount_paid, reading.meter_billings_sum_credit, reading.bill)) | formatCurrency }}</td>
              <td>
                <v-chip
                  text-color="white"
                  :class="{
                    'paid': reading.status === 1,
                    'not-paid': reading.status === 0,
                    'balance': reading.status === 2,
                    'over-paid': reading.status === 3,
                  }"
                >
                  {{ reading.status | formatMeterReadingStatus }}
                </v-chip>
              </td>
              <td @click.stop>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      ma-2
                      v-bind="attrs"
                      text
                      icon
                      @click="getMeterReadingsPreviewMessage(reading.id)"
                      v-on="on"
                    >
                      <v-icon
                        v-bind="attrs"
                        v-on="on"
                      >
                        {{ icons.mdiEye }}
                      </v-icon>
                    </v-btn>
                  </template>
                  <span>Preview message</span>
                </v-tooltip>
                <v-tooltip
                  v-if="can('meter-reading-edit')"
                  bottom
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      ma-2
                      v-bind="attrs"
                      text
                      icon
                      @click="openCorrections(reading)"
                      v-on="on"
                    >
                      <v-icon
                        v-bind="attrs"
                        v-on="on"
                      >
                        {{ icons.mdiPencil }}
                      </v-icon>
                    </v-btn>
                  </template>
                  <span>Edit</span>
                </v-tooltip>
                <v-tooltip
                  v-if="can('meter-reading-edit')"
                  bottom
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      ma-2
                      v-bind="attrs"
                      text
                      icon
                      @click="showResendMeterReadingDialog(reading.id, reading.user_name, reading.month)"
                      v-on="on"
                    >
                      <v-icon
                        v-bind="attrs"
                        v-on="on"
                      >
                        {{ icons.mdiSend }}
                      </v-icon>
                    </v-btn>
                  </template>
                  <span>Resend message</span>
                </v-tooltip>
                <v-tooltip
                  v-if="can('meter-reading-delete')"
                  bottom
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      ma-2
                      v-bind="attrs"
                      text
                      icon
                      @click="showDeleteMeterDialog(reading.id)"
                      v-on="on"
                    >
                      <v-icon
                        v-bind="attrs"
                        v-on="on"
                      >
                        {{ icons.mdiDelete }}
                      </v-icon>
                    </v-btn>
                  </template>
                  <span>Delete</span>
                </v-tooltip>
              </td>
            </tr>
          </tbody>
          <tbody v-else>
            <tr v-show="!isMeterReadingsLoading">
              <td
                :colspan="headers.length"
                style="text-align: center"
              >
                No meter readings found
              </td>
            </tr>
          </tbody>
        </template>
      </v-data-table>
      <v-divider />
      <data-table-pagination
        :page="pagination.page"
        :page-count="pagination.pageCount"
        @page-change="onPageChange"
        @items-per-page-change="onItemsPerPageChange"
      />
    </v-card>
    <correct-reading-history-dialog
      :show-dialog="showCorrections"
      :customer-id="correctionCustomerId"
      :account-number="correctionAccountNumber"
      :initial-month="correctionMonth"
      @close="showCorrections = false"
      @saved="onCorrectionsSaved"
    />
    <add-meter-reading-dialog
      :show-dialog="showAddMeterDialog"
      :meter-reading="meterReading"
      @close="closeAddMeterDialog()"
      @meter-reading-added="onMeterReadingAdded"
      @meter-reading-updated="onMeterReadingUpdated"
    />
    <confirm-dialog
      :show-dialog="showConfirmDeleteDialog"
      :is-agree-button-loading="isConfirmDeleteDialogButtonLoading"
      :agree-text="'Delete'"
      @cancel="showConfirmDeleteDialog = false"
      @agree="deleteMeterReading()"
    />
    <confirm-dialog
      :show-dialog="showConfirmResendDialog"
      :is-agree-button-loading="isConfirmResendDialogButtonLoading"
      :agree-text="'Resend'"
      :message="resendTokenMessage"
      @cancel="showConfirmResendDialog = false"
      @agree="resendMeterReading()"
    />
    <v-dialog
      v-model="showPreviewMeterReadingsMessageDialog"
      max-width="450px"
    >
      <v-card>
        <v-card-text id="print-area">
          <h3 class="text-center">
            Major M Community Water Project
          </h3>
          <h4 class="text-center">
            0711900600 / 0785066614/ 0722520114
          </h4><br>
          <meter-reading-preview-message-shimmer v-show="isGetMeterReadingsPreviewMessageLoading" />
          <p
            style="white-space: pre-line"
            class="pt-4"
          >
            {{ meterReadingsPreviewMessage | formatMeterReadingMessage }}
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="blue darken-1"
            text
            @click="showPreviewMeterReadingsMessageDialog = false"
          >
            Close
          </v-btn>
          <v-btn
            color="blue darken-1"
            text
            @click="printReceipt"
          >
            Print
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="showReadingEditDialog"
      max-width="420px"
    >
      <v-card v-if="selectedReadingEdit">
        <v-card-title class="pb-2">
          <span class="subtitle-1">Reading Edit History</span>
          <v-spacer />
          <v-btn
            icon
            @click="showReadingEditDialog = false"
          >
            <v-icon>{{ icons.mdiClose }}</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-4">
          <div class="d-flex justify-space-between align-center mb-3">
            <span class="grey--text">Previous Value</span>
            <span class="font-weight-medium red--text text--darken-1">{{ selectedReadingEdit.previous_value }}</span>
          </div>
          <div class="d-flex justify-space-between align-center mb-3">
            <span class="grey--text">New Value</span>
            <span class="font-weight-medium green--text text--darken-2">{{ selectedReadingEdit.new_value }}</span>
          </div>
          <v-divider class="mb-3" />
          <div class="d-flex justify-space-between align-center mb-1">
            <span class="grey--text">Edited By</span>
            <span class="font-weight-medium">{{ selectedReadingEdit.edited_by }}</span>
          </div>
          <div class="d-flex justify-space-between align-center">
            <span class="grey--text">Edited At</span>
            <span class="font-weight-medium">{{ selectedReadingEdit.edited_at | formatDate }}</span>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="showReadingEditDialog = false"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from 'axios'
import _ from 'lodash'
import {
  mdiMagnify, mdiPencil, mdiSend, mdiDelete, mdiEye, mdiHistory, mdiClose,
} from '@mdi/js'
import moment from 'moment'
import CorrectReadingHistoryDialog from '@/components/dialogs/CorrectReadingHistoryDialog.vue'
import hasPermission from '@/mixins/hasPermission'
import printHtml from '@/mixins/printHtml'
import AddMeterReadingDialog from '@/components/dialogs/AddMeterReadingDialog.vue'
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue'
import SearchInput from '@/components/partials/SearchInput.vue'
import TableFilter from '../partials/table-filter/index.vue'
import RefreshButton from '@/components/partials/RefreshButton.vue'
import MeterReadingPreviewMessageShimmer from '../partials/shimmers/MeterReadingPreviewMessageShimmer.vue'
import defaultMonthFilterDate from '@/mixins/defaultMonthFilterDate'
import DataTablePagination from '@/components/partials/DataTablePagination.vue'
import ExportButton from '@/components/partials/ExportButton.vue'

export default {
  components: {
    CorrectReadingHistoryDialog,
    AddMeterReadingDialog,
    ConfirmDialog,
    SearchInput,
    TableFilter,
    RefreshButton,
    MeterReadingPreviewMessageShimmer,
    DataTablePagination,
    ExportButton,
  },
  filters: {
    formatMeterReadingMessage(value) {
      const messageChunks = value.split('Pay via')

      return `${messageChunks[0]}\nPay via ${messageChunks[1]}`
    },
  },
  mixins: [hasPermission, defaultMonthFilterDate, printHtml],
  props: {
    customerId: { type: String, default: '' },
    meterReadingUrl: {
      type: String,
      default: null,
    },
    clickableRows: {
      type: Boolean,
      default: true,
    },
    showAddMeterDialog: {
      type: Boolean,
      default: false,
    },
    forSpecificCustomer: {
      type: Boolean,
      default: false,
    },
    defaultSortBy: {
      type: String,
      default: 'account_number',
    },
    defaultSortDesc: {
      type: Boolean,
      default: false,
    },
    accountNumber: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      showCorrections: false,
      correctionCustomerId: '',
      correctionAccountNumber: '',
      correctionMonth: '',
      showConfirmDeleteDialog: false,
      showConfirmResendDialog: false,
      showPreviewMeterReadingsMessageDialog: false,
      showReadingEditDialog: false,
      selectedReadingEdit: null,
      isConfirmDeleteDialogButtonLoading: false,
      isConfirmResendDialogButtonLoading: false,
      toDelete: null,
      sortBy: [this.defaultSortBy],
      sortDesc: [this.defaultSortDesc],
      pagination: {
        page: 1,
        pageCount: 1,
        itemsPerPage: 10,
      },
      search: '',
      headers: [
        {
          text: 'Account Number',
          value: 'account_number',
        },
        {
          text: 'Customer',
          value: 'user',
          sortable: false,
        },
        {
          text: 'Previous Reading',
          value: 'previous_reading',
        },
        {
          text: 'Current Reading',
          value: 'current_reading',
        },
        {
          text: 'Billed units',
          value: 'units_consumed',
        },
        {
          text: 'Month',
          value: 'month',
        },
        {
          text: 'Date Added',
          value: 'created_at',
        },
        { text: 'Bill', value: 'bill' },
        { text: 'Amount paid', value: 'amount_paid' },
        { text: 'Credit applied', value: 'credit' },
        { text: 'Balance', value: 'balance' },
        { text: 'Status', value: 'status' },
        { text: 'Actions', value: 'actions', sortable: false },
      ],
      activeSearchFilter: 'users.account_number',
      searchFilters: [
        {
          text: 'Account number',
          value: 'users.account_number',
        },
        {
          text: 'Customer',
          value: 'users.name',
        },
      ],
      icons: {
        mdiMagnify,
        mdiPencil,
        mdiSend,
        mdiDelete,
        mdiEye,
        mdiHistory,
        mdiClose,
      },
      meterReading: {},
      meterReadings: [],
      isMeterReadingsLoading: true,
      isMeterReadingsRefreshing: false,
      isGetMeterReadingsPreviewMessageLoading: false,
      toResend: '',
      toResendMonth: '',
      toResendCustomerName: '',
      filterLabel: '',
      filters: {
        yearFrom: '',
        yearTo: '',
        month: '',
        paymentStatus: [],
      },
      exportTableHeaders: [
        { title: 'Account Number', dataKey: 'account_number' },
        { title: 'Customer', dataKey: 'user_name' },
        { title: 'Previous Reading', dataKey: 'previous_reading' },
        { title: 'Current Reading', dataKey: 'current_reading' },
        { title: 'Billed Units', dataKey: 'units_consumed' },
        { title: 'Reading Basis', dataKey: 'reading_basis' },
        { title: 'Month', dataKey: 'month' },
        { title: 'Bill', dataKey: 'bill', type: 'number' },
      ],
      meterReadingsPreviewMessage: '',
      totalExpectedAmount: 0,
      totalAmountPaid: 0,
    }
  },
  computed: {
    sortOrder() {
      return this.sortDesc[0] ? 'desc' : 'asc'
    },
    refreshTable() {
      return `${this.meterReadingUrl}|${this.search}|${this.sortBy}|${this.sortDesc}`
    },
    resendTokenMessage() {
      const monthName = moment(String(this.toResendMonth)).format('MMMM YYYY')

      return `Are you sure you want to resend ${monthName} meter reading message to ${this.toResendCustomerName}?`
    },
  },
  watch: {
    refreshTable() {
      this.getMeterReadings()
    },
    activeSearchFilter() {
      if (this.search !== '') {
        this.getMeterReadings()
      }
    },
  },
  mounted() {
    if (this.forSpecificCustomer) {
      this.filters.yearFrom = (new Date().getFullYear() - 1).toString()
      this.filters.yearTo = new Date().getFullYear().toString()
    } else {
      this.filters.month = this.defaultMonthDate.format('YYYY-MM')
    }
    this.getMeterReadings()
    if (!this.clickableRows) {
      this.removeFirstTwoTableHeader()
    }
  },
  methods: {
    billedUnits(reading) { return reading.adjusted_units != null ? reading.adjusted_units : reading.current_reading - reading.previous_reading },
    openCorrections(reading = null) {
      this.correctionCustomerId = reading ? reading.user_id : this.customerId
      this.correctionAccountNumber = reading ? reading.account_number : this.accountNumber
      this.correctionMonth = reading ? reading.month : ''
      this.showCorrections = true
    },
    onCorrectionsSaved() {
      this.refreshMeterReadings()
      this.$emit('readings-corrected')
    },
    showResendMeterReadingDialog(id, name, month) {
      this.showConfirmResendDialog = true
      this.toResend = id
      this.toResendMonth = month
      this.toResendCustomerName = name
    },
    constructMeterReadingsUrl() {
      const status = JSON.stringify(this.filters.paymentStatus)

      return `${this.meterReadingUrl}&month=${this.filters.month}&year_from=${this.filters.yearFrom || ''}&year_to=${this.filters.yearTo || ''}&sortBy=${this.sortBy[0]}&sortOrder=${this.sortOrder}&search=${this.search}&search_filter=${this.activeSearchFilter}&status=${status}&page=${this.pagination.page}&perPage=${this.pagination.itemsPerPage}`
    },
    constructExportTitle() {
      const accountPrefix = this.accountNumber ? `${this.accountNumber} ` : ''
      if (this.forSpecificCustomer) {
        return `${accountPrefix}Meter Readings ${this.filters.yearFrom} - ${this.filters.yearTo}`
      }

      return `${accountPrefix}Meter Readings ${this.filters.month}`
    },
    getMeterReadings: _.debounce(function () {
      this.isMeterReadingsLoading = true
      axios
        .get(this.constructMeterReadingsUrl())
        .then(response => {
          this.totalExpectedAmount = response.data.total_bill
          this.totalAmountPaid = response.data.total_amount_paid
          this.meterReadings = response.data.meter_readings.data
          this.pagination.page = response.data.meter_readings.current_page
          this.pagination.pageCount = response.data.meter_readings.last_page
          this.isMeterReadingsLoading = false
          this.isMeterReadingsRefreshing = false
        })
        .catch(error => {
          this.isMeterReadingsLoading = false
          this.isMeterReadingsRefreshing = false
          this.$notification.error(error.response.data.message)
        })
    }, 500),
    getMeterReadingsPreviewMessage(meterReadingId) {
      this.meterReadingsPreviewMessage = ''
      this.showPreviewMeterReadingsMessageDialog = true
      this.isGetMeterReadingsPreviewMessageLoading = true
      axios
        .get(`meter-readings-preview-message/${meterReadingId}`)
        .then(response => {
          this.meterReadingsPreviewMessage = response.data.message
          this.isGetMeterReadingsPreviewMessageLoading = false
        })
        .catch(error => {
          this.$notification.error(error.response.data.message)
          this.isGetMeterReadingsPreviewMessageLoading = false
        })
    },
    onMeterReadingUpdated(reading) {
      if (!reading.hasMessageBeenResent && this.can('meter-reading-edit')) {
        this.showResendMeterReadingDialog(reading.id, reading.userName, reading.month)
      }
    },
    onMeterReadingAdded(month) {
      if (month !== this.filters.month) {
        this.filters.month = month
        this.filterLabel = month
      }
      this.refreshMeterReadings()
    },
    refreshMeterReadings() {
      this.isMeterReadingsRefreshing = true
      this.getMeterReadings()
    },
    resendMeterReading() {
      this.isConfirmResendDialogButtonLoading = true
      axios
        .post(`meter-readings-resend/${this.toResend}`)
        .then(() => {
          this.$notification.success(`Meter reading message has been resend to ${this.toResendCustomerName}`)
          this.isConfirmResendDialogButtonLoading = false
          this.showConfirmResendDialog = false
        })
        .catch(error => {
          this.$notification.error(error.response.data.message)
          this.isConfirmResendDialogButtonLoading = false
          this.showConfirmResendDialog = false
        })
    },
    editMeter(meterReading) {
      this.meterReading = meterReading
      this.$emit('add-meter-dialog-open')
    },
    showDeleteMeterDialog(id) {
      this.showConfirmDeleteDialog = true
      this.toDelete = id
    },
    deleteMeterReading() {
      this.isConfirmDeleteDialogButtonLoading = true
      axios
        .delete(`meter-readings/${this.toDelete}`)
        .then(() => {
          this.$notification.success('Meter reading deleted successfully')
          this.isConfirmDeleteDialogButtonLoading = false
          this.showConfirmDeleteDialog = false
          this.getMeterReadings()
        })
        .catch(error => {
          this.$notification.error(error.response.data.message)
          this.isConfirmDeleteDialogButtonLoading = false
          this.showConfirmDeleteDialog = false
        })
    },
    onMeterReadingClick(meterReadingId) {
      this.$router.push({ name: 'meter-readings-details', params: { id: meterReadingId } })
    },
    viewReadingEditDetails(reading) {
      this.selectedReadingEdit = reading.last_reading_edit
      this.showReadingEditDialog = true
    },
    closeAddMeterDialog() {
      this.meterReading = {}
      this.$emit('add-meter-dialog-close')
    },
    removeFirstTwoTableHeader() {
      this.headers.splice(0, 2)
    },
    onPageChange(page) {
      this.pagination.page = page
      this.getMeterReadings()
    },
    onItemsPerPageChange(itemsPerPage) {
      this.pagination.itemsPerPage = itemsPerPage
      this.getMeterReadings()
    },
    onSearchFilterChange(filter) {
      this.activeSearchFilter = filter
    },
    applyFilters(filters) {
      this.filters = filters
      this.getMeterReadings()
    },
    transformExportRow(row) {
      return { ...row, units_consumed: this.billedUnits(row), reading_basis: row.adjusted_units != null ? 'Estimated' : 'Actual' }
    },
    calculateBalance(amountPaid, creditApplied, bill) {
      const amount = parseFloat(amountPaid) || 0
      const credit = parseFloat(creditApplied) || 0
      const balance = parseFloat(bill) - (amount + credit)

      if (balance < 0) {
        return 0
      }

      return balance
    },
  },
}
</script>

<style scoped>
.clickable {
  cursor: pointer;
}
.v-chip.not-paid {
  background: #F44336;
}
.v-chip.paid {
  background: #4CAF50;
}
.v-chip.balance {
  background: #FFA726;
}
.v-chip.over-paid {
  background: #2196F3;
}
.v-chip.edited-chip {
  background: #FFF3E0;
  color: #E65100;
}
.v-chip.edited-chip .v-icon {
  color: #E65100;
}
.theme--dark.v-chip.edited-chip {
  background: rgba(255, 152, 0, 0.18);
  color: #FFB74D;
}
.theme--dark.v-chip.edited-chip .v-icon {
  color: #FFB74D;
}
</style>
